import Axios, { AxiosError } from 'axios';

import { Region } from '@components/common/constants';
import { IS_ADMIN_WEB_APP } from '@config';
import { FeatureFlagKeys, experiment } from '@lib/experiment';
import { getBackendUrl } from '@services/utils';

/**
 * The shared session of the web app and Lookout (ACT2-879). config-backend keeps it for the browser in an HttpOnly
 * cookie on the API host, and the page asks it for short-lived ID tokens.
 */

/**
 * How long the page waits for the feature flags. After that, only a page that already uses the shared session keeps
 * it. A returning browser reads its cached flags at once, so only a first visit waits for Flagsmith.
 */
const FLAG_WAIT_MS = 1000;
const REQUEST_TIMEOUT_MS = 30000;
const CHANNEL_NAME = 'rudderstack_shared_session';

/**
 * - `token`: a valid ID token of the shared session.
 * - `ended`: config-backend has no session for this browser, or the session ended.
 * - `refused`: config-backend does not trust this site's origin, so its sign-ins fail too.
 * - `unavailable`: config-backend did not answer. The next check tries again.
 */
type SessionTokenResult =
  | { kind: 'token'; idToken: string }
  | { kind: 'ended' }
  | { kind: 'refused' }
  | { kind: 'unavailable' };

/**
 * A sign-in that could not use the shared session. It has the shape of an API error, so each sign-in page shows its
 * message.
 */
export class SharedSessionSignInError extends Error {
  readonly response: { data: { message: string } };

  constructor(message: string) {
    super(message);
    this.name = 'SharedSessionSignInError';
    this.response = { data: { message } };
  }
}

export const UNTRUSTED_SITE_MESSAGE = 'RudderStack does not allow sign-in from this site.';

interface TokenOptions {
  /** The token stays valid for at least this long. config-backend allows up to 30 minutes. */
  minValiditySeconds?: number;
  timeoutMs?: number;
}

let enabled = false;
/** config-backend refused this site's origin at start, so every sign-in would fail CORS. */
let refusedOrigin = false;
const pendingTokens = new Map<string, Promise<SessionTokenResult>>();

const post = <T>(path: string, body: object, timeoutMs = REQUEST_TIMEOUT_MS) =>
  Axios.post<T>(`${getBackendUrl(Region.US)}${path}`, body, {
    withCredentials: true,
    timeout: timeoutMs,
    headers: { 'Content-Type': 'application/json' },
  });

const statusOf = (error: unknown) =>
  Axios.isAxiosError(error) ? error.response?.status : undefined;

/**
 * Resolves true when the flags loaded, and false after a timeout or a failed Flagsmith start. Without Flagsmith, for
 * example on a local stack, the local defaults count as loaded.
 */
const waitForFlags = async (): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), FLAG_WAIT_MS);
  });
  const loaded = (experiment.initializationPromise ?? Promise.resolve()).then(
    // A failed Flagsmith start resolves too, so the page also checks that Flagsmith started.
    () => !experiment.initializationPromise || experiment.hasInitialized,
    () => false,
  );
  try {
    return await Promise.race([loaded, timeout]);
  } finally {
    clearTimeout(timer);
  }
};

const isNetworkError = (error: unknown) =>
  Axios.isAxiosError(error) && error.code === AxiosError.ERR_NETWORK;

/**
 * CORS hides config-backend's 403 from a site that it does not trust, because a call with credentials needs the
 * credentials header. A call without credentials shows the answer to any site.
 */
const isRefusedOrigin = async (timeoutMs: number) => {
  try {
    await Axios.post(
      `${getBackendUrl(Region.US)}/session/token`,
      {},
      { timeout: timeoutMs, headers: { 'Content-Type': 'application/json' } },
    );
    return false;
  } catch (error) {
    return statusOf(error) === 403;
  }
};

const requestToken = async ({
  minValiditySeconds,
  timeoutMs = REQUEST_TIMEOUT_MS,
}: TokenOptions): Promise<SessionTokenResult> => {
  try {
    const { data } = await post<{ idToken?: unknown }>(
      '/session/token',
      minValiditySeconds === undefined ? {} : { minValiditySeconds },
      timeoutMs,
    );
    return typeof data?.idToken === 'string' && data.idToken
      ? { kind: 'token', idToken: data.idToken }
      : { kind: 'unavailable' };
  } catch (error) {
    const status = statusOf(error);
    if (status === 401) {
      return { kind: 'ended' };
    }
    if (status === 403 || status === 415) {
      return { kind: 'refused' };
    }
    if (isNetworkError(error) && (await isRefusedOrigin(timeoutMs))) {
      return { kind: 'refused' };
    }
    return { kind: 'unavailable' };
  }
};

export const sharedSession = {
  /**
   * Decides once, before sign-in, whether this page uses the shared session. The admin web app never does. When the
   * flags do not load in time, a page that already uses the shared session keeps it, so a slow Flagsmith signs no one
   * out.
   */
  async init({ hasSharedSession = false }: { hasSharedSession?: boolean } = {}): Promise<boolean> {
    refusedOrigin = false;
    if (IS_ADMIN_WEB_APP) {
      enabled = false;
      return false;
    }
    enabled = (await waitForFlags())
      ? experiment.isFeatureEnabled(FeatureFlagKeys.enableLookoutSharedAuthSession)
      : hasSharedSession;
    return enabled;
  },

  isEnabled(): boolean {
    return enabled;
  },

  /**
   * The current value of the flag. A page that runs without the shared session reads it before it calls a session
   * route, so that with the flag off it calls none, as before the shared session. The admin web app never uses the
   * shared session.
   */
  isFlagOn(): boolean {
    return (
      !IS_ADMIN_WEB_APP &&
      experiment.isFeatureEnabled(FeatureFlagKeys.enableLookoutSharedAuthSession)
    );
  },

  /** `POST /session/token`. Calls with the same options that start together share one request. */
  getToken(options: TokenOptions = {}): Promise<SessionTokenResult> {
    const key = JSON.stringify(options);
    let pending = pendingTokens.get(key);
    if (!pending) {
      pending = requestToken(options)
        .then((result) => {
          if (result.kind === 'refused') {
            refusedOrigin = true;
          }
          return result;
        })
        .finally(() => {
          pendingTokens.delete(key);
        });
      pendingTokens.set(key, pending);
    }
    return pending;
  },

  /** `POST /session/federated` after a Google or SSO sign-in. Returns the ID token that config-backend refreshed. */
  async startFederated(tokens: { idToken: string; refreshToken: string }): Promise<string> {
    if (refusedOrigin) {
      throw new SharedSessionSignInError(UNTRUSTED_SITE_MESSAGE);
    }
    const { data } = await post<{ idToken?: unknown }>('/session/federated', {
      idToken: tokens.idToken,
      refreshToken: tokens.refreshToken,
    });
    if (typeof data?.idToken !== 'string' || !data.idToken) {
      throw new Error('The shared session did not start');
    }
    return data.idToken;
  },

  /**
   * `POST /session/signout`. For Google and SSO, config-backend names the Hosted UI sign-out page. The admin web app
   * never uses the shared session, so it ends nothing.
   */
  async end({ timeoutMs }: { timeoutMs?: number } = {}): Promise<{
    hostedUiSignOutUrl: string | null;
  }> {
    if (IS_ADMIN_WEB_APP) {
      return { hostedUiSignOutUrl: null };
    }
    const { data } = await post<{ hostedUiSignOutUrl?: unknown }>(
      '/session/signout',
      {},
      timeoutMs,
    );
    return {
      hostedUiSignOutUrl:
        typeof data?.hostedUiSignOutUrl === 'string' ? data.hostedUiSignOutUrl : null,
    };
  },
};

/**
 * The request body that asks `/login` or `/v1/mfa/method` to start the shared session, and its credentials. On a site
 * that config-backend does not trust, the sign-in fails at once with a clear message.
 */
export const sharedSessionSignIn = () => {
  if (!enabled) {
    return { body: {}, config: {} };
  }
  if (refusedOrigin) {
    throw new SharedSessionSignInError(UNTRUSTED_SITE_MESSAGE);
  }
  return { body: { sharedSession: true }, config: { withCredentials: true } };
};

/** Tells the other tabs of this site that the user signed out of the shared session. */
export const announceSharedSignOut = () => {
  if (typeof window.BroadcastChannel !== 'function') {
    return;
  }
  const channel = new window.BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'signed_out' });
  channel.close();
};

/** Calls `listener` when another tab of this site signs out of the shared session. */
export const onSharedSignOutElsewhere = (listener: () => void) => {
  if (typeof window.BroadcastChannel !== 'function') {
    return () => undefined;
  }
  const channel = new window.BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
    if (event.data?.type === 'signed_out') {
      listener();
    }
  };
  return () => channel.close();
};
